import { dtmfKindLabel } from "../lib/digits";
import { consoleEmoji, field, formatPark, heroCopy, heroTone } from "../lib/hero";
import { AMD_OPERATOR_LABEL, historyAmd } from "../lib/historyOutcome";
import { useStore } from "../lib/store";
import { cx } from "./ui";

const TONE: Record<string, string> = {
  ready: "text-mute",
  live: "text-live",
  parked: "text-park",
  failed: "text-fail",
};

/**
 * Two-line operator hero: human copy + party line. Never paints raw state names.
 */
export function CallHero() {
  const { state, abnormal } = useStore();
  const { snapshot } = state;
  const copy = snapshot.heroCopy || heroCopy(snapshot.state, snapshot.detail);
  const tone = heroTone(snapshot.state, snapshot.party.hangup_cause, abnormal);
  const { name, dest, cli, attempt, max_attempts, park_remaining_s } = snapshot.party;
  const parked = snapshot.state === "PARKED";
  const rawAmd = snapshot.amd_label || snapshot.party.amd;
  const emoji = consoleEmoji(snapshot.state, rawAmd);
  const canon = historyAmd(rawAmd);
  const amdCopy = canon ? AMD_OPERATOR_LABEL[canon] : field(rawAmd);
  const farEndClass = snapshot.far_end_class;
  // CDR judgment is an expected-code match signal — never label it "Outcome".
  const codeMatch = snapshot.judgment_outcome;
  const trunkId = snapshot.trunk_id;
  const scriptLabel = snapshot.scriptId
    ? snapshot.scriptNode
      ? `${snapshot.scriptId} · ${snapshot.scriptNode}`
      : snapshot.scriptId
    : "—";
  const dtmfKind = dtmfKindLabel(snapshot.state, snapshot.detail, snapshot.digitKind);
  const spoken = snapshot.spokenLanguage;

  return (
    <section className="px-4 pt-4 pb-1 md:px-6 md:pt-5">
      <div className={cx("flex items-baseline gap-2.5 text-[28px] font-medium tracking-tight md:text-[32px]", TONE[tone])}>
        {emoji ? (
          <span className="text-[20px] leading-none opacity-80" aria-hidden>
            {emoji}
          </span>
        ) : null}
        <span>
          {copy}
          {snapshot.state === "ACCEPT_GATE" ? " · 1 Accept · 2 Retry" : ""}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[12px] text-ink-2">
        <span className="text-ink">
          {field(name)} <span className="text-mute">·</span> {field(dest)}
        </span>
        <span>
          CLI <span className="text-ink">{field(cli)}</span>
        </span>
        <span>
          AMD <span className="text-ink">{amdCopy}</span>
        </span>
        <span>
          Script <span className="text-ink">{scriptLabel}</span>
        </span>
        <span>
          DTMF <span className="text-ink">{dtmfKind}</span>
        </span>
        {spoken ? (
          <span>
            Lang <span className="text-ink">{spoken}</span>
          </span>
        ) : null}
        <span>
          Class <span className="text-ink">{field(farEndClass)}</span>
        </span>
        {codeMatch ? (
          <span
            className="text-mute"
            title="Expected-code match judgment from the CDR — not the call outcome"
          >
            code-match <span className="text-ink">{field(codeMatch)}</span>
          </span>
        ) : null}
        {trunkId ? (
          <span>
            Trunk <span className="text-ink">{field(trunkId)}</span>
          </span>
        ) : null}
        <span className="tabular-nums">
          {attempt}/{max_attempts}
        </span>
        {parked ? (
          <span className="tabular-nums text-park">park {formatPark(park_remaining_s)}</span>
        ) : null}
      </div>
    </section>
  );
}
